import React, { useEffect, useState } from "react";
import {
  Dialog,
  Input,
  Flex,
  Text,
  Loader,
  Alert,
  Label,
} from "@fluentui/react-northstar";
import {
  SearchIcon,
  CloseIcon,
  LikeIcon,
  DislikeIcon,
  QuestionCircleIcon,
} from "@fluentui/react-icons-northstar";
import { api } from "../../../../../../Assets/api/api";
import { useIntelligentGrading } from "../../../../context/useIntelligentGrading";

const tagStyles = {
  positive: { color: "#92C353", value: "Positive" },
  suggestion: { color: "#F8D347", value: "Suggestion" },
  issue: { color: "#E2726E", value: "Issue" },
  grammar: { color: "#8B9DC3", value: "Grammar" },
  structure: { color: "#6264A7", value: "Structure" },
  citation: { color: "#FF8C00", value: "Citation" },
};

const CommentBankDialog = ({ isOpen, onDismiss, onSelectComment }) => {
  const { user } = useIntelligentGrading();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `/api/comment-bank/${user.mail}${user.slug}`
        );
        if (response.data?.result) {
          setComments(response.data.result);
        }
      } catch (err) {
        setError("Failed to fetch comments from comment bank");
        console.error("Error fetching comments:", err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && user) {
      fetchComments();
    }
  }, [isOpen, user]);

  const filteredComments = comments.filter(
    (comment) =>
      comment.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comment.tag.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderComment = (item) => {
    const tagStyle = tagStyles[item.tag.toLowerCase()] || tagStyles.neutral;

    return (
      <Flex
        key={item.id}
        onClick={() => onSelectComment(item)}
        data-cy="comment-bank-item"
        gap="gap.small"
        styles={{
          cursor: "pointer",
          ":hover": {
            backgroundColor: "rgba(0,0,0,0.05)",
          },
        }}
      >
        <Flex.Item>
          <Label
            circular
            styles={{
              backgroundColor: `${tagStyle.color}20`,
              color: tagStyle.color,
            }}
            content={item.tag}
          />
        </Flex.Item>
        <Flex.Item grow>
          <Text content={item.text} />
        </Flex.Item>
      </Flex>
    );
  };

  const renderDialogContent = () => {
    if (loading) {
      return (
        <Flex
          column
          fill
          hAlign="center"
          vAlign="center"
          padding="padding.medium"
        >
          <Loader label="Loading comments..." />
        </Flex>
      );
    }

    if (error) {
      return (
        <Flex column fill padding="padding.medium">
          <Alert
            danger
            content={error}
            dismissible
            dismissAction={{
              "aria-label": "Close error message",
              onClick: () => setError(null),
            }}
          />
        </Flex>
      );
    }

    if (filteredComments.length === 0) {
      return (
        <Flex
          column
          fill
          hAlign="center"
          vAlign="center"
          padding="padding.medium"
        >
          <Text content="No comments found" size="large" />
        </Flex>
      );
    }

    return (
      <Flex column gap="gap.small">
        <div>
          <Input
            icon={<SearchIcon />}
            placeholder="Search comments..."
            value={searchQuery}
            onChange={(e, { value }) => setSearchQuery(value || "")}
            clearable
          />
        </div>
        <Flex
          column
          gap="gap.smaller"
          style={{ overflowY: "auto", maxHeight: "40vh" }}
        >
          {filteredComments.map(renderComment)}
        </Flex>
      </Flex>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpen={() => { }}
      onCancel={onDismiss}
      header="Comment Bank"
      content={renderDialogContent()}
      headerAction={{
        icon: <CloseIcon />,
        title: "Close",
        onClick: onDismiss,
      }}
    />
  );
};

export default CommentBankDialog;
